import { create } from 'zustand'
import type {
  Asset,
  AudioClip,
  AutoEditParams,
  AutoEditSettings,
  Composition,
  CompositionMeta,
  Job,
  Overlay,
  OutputSpec,
  Project,
  Scene,
  Section,
  Segment,
  TemplateId,
} from '@shared/types'
import { api } from '../api'
import { makeId } from '../lib/id'
import { makeEmptyComposition } from '../api/mockFixtures'
import { HistoryStack } from '../lib/history'
import {
  buildSplitSegments,
  clampPxPerSec,
  computeSplit,
  getSegmentTimeline,
  getTotalDurationSec,
  pruneSelection,
  resolveSplitTarget,
} from '../lib/timelineMath'

export type SaveState = 'idle' | 'saving' | 'saved' | 'error'
/**
 * UI MK2 第2段 (UI_MK2_PHASE2.md §3-1/§3-7): 右パネルが選択駆動になったので
 * タイトル/ラベル/終了カードの専用タブは廃止 ── その一覧・追加機能は
 * ControlPanel/ElementsList.tsx（未選択時のみ表示）へ統合した。Timeline/Preview
 * 側はこの型を参照していない(2026-09-05 grep 済み・ControlPanel/**内でのみ使用)。
 */
export type ControlTab = 'template' | 'sections' | 'audio' | 'export'

/** ひとつの選択対象。UI MK2 第1段: 複数選択できるよう `selectedIds: SelectionItem[]` に配列化した。 */
export type SelectionItem =
  | { kind: 'segment'; id: string }
  | { kind: 'overlay'; id: string }
  | { kind: 'audio'; id: string }

/**
 * レガシー単一選択（`selectedIds[0] ?? null` のミラー）。第1段導入時点では
 * `TabLabels.tsx` 等の旧タブが `s.selection?.kind` を直接読んでいたが、
 * それらは UI_MK2_PHASE2.md §3-7 で選択駆動インスペクタ（OverlayInspector/**）
 * に置き換わり削除済み — 2026-09-05 時点の grep では現行コードに直接の
 * 読み手は見つからない（未確認の外部依存があり得るため、実害が無いと確認
 * できるまで念のため書き込みは維持する）。
 */
export type Selection = SelectionItem | null

export interface UploadEntry {
  id: string
  fileName: string
  loaded: number
  total: number
  status: 'uploading' | 'processing' | 'done' | 'error'
  assetId?: string
  error?: string
}

interface EditorState {
  // navigation
  screen: 'projects' | 'editor'

  // projects list
  projects: Project[]
  projectsLoading: boolean
  projectsError: string | null

  // current project/editor
  currentProject: Project | null
  assets: Record<string, Asset>
  assetOrder: string[]
  composition: Composition | null
  editorLoading: boolean
  editorError: string | null

  saveState: SaveState
  saveError: string | null

  uploads: Record<string, UploadEntry>

  ingestJobs: Record<string, Job> // jobId -> Job, for assets currently processing
  autoEditRunning: boolean
  autoEditError: string | null
  /** Non-fatal engine diagnostics from the last auto-edit run (e.g. footage shortage). Never dropped silently. */
  autoEditWarnings: string[]
  /**
   * reviewer 6/10 m2: set when `openProject`'s deep-link `compositionId`
   * (docs/COMPOSER_CONTRACT.md §6.1, e.g. a ComposerDraft's `studioUrl`)
   * doesn't match any Composition in the opened Project — it used to fall
   * back to `compositions[0]` with no indication anything was wrong. `null`
   * = nothing to show (cleared on every fresh `openProject` call).
   */
  deepLinkNotice: string | null

  previewJob: Job | null
  previewUrl: string | null
  renderJob: Job | null
  /**
   * P10-3 担当A (docs/P10_PHASE3.md §2 item 6): now the raw `Job[]` (was
   * `{jobId, resultPath, createdAt}[]`) — ExportHistory.tsx needs the whole
   * Job (`.status`/`.error`/`.result` for size/尺/容量/エラー表示), not just
   * enough to build a download link. Composition-scoped (kind:'render');
   * ExportHistory's own「プロジェクト全体」scope fetches project-wide via
   * `api.listProjectJobs` separately (component-local state, not this field).
   */
  renderHistory: Job[]

  selection: Selection
  /** UI MK2 第1段: 実体はこちら。`selection` はこの先頭要素のミラー（互換用）。 */
  selectedIds: SelectionItem[]
  activeTab: ControlTab
  expandedAssetIds: Record<string, boolean>

  // ---- UI MK2 第1段: タイムライン UI 状態 ----
  /** null = 「全体を表示」(fit)。ユーザーが明示的にズームすると数値が入る。 */
  zoomPxPerSec: number | null
  /** Timeline コンポーネントが実際に描画に使った px/sec の鏡写し（fit 解決後の実値）。
   *  ⌘+/⌘−・ツールバーのスライダーが「今どのくらいの倍率か」を知るために読む。 */
  effectivePxPerSec: number
  /** 吸着 (N でトグル)。既定 ON。 */
  snapEnabled: boolean
  /** タイムライン上の一時的なお知らせ（例:「端では分割できません」）。inline 表示・自動で消える。 */
  timelineNotice: string | null
  /** ドラッグ/トリム中に実際に吸着した秒（無ければ null）。ガイド線の描画に使う。 */
  snapGuideSec: number | null
  /** Undo/Redo ボタンの disabled 判定用（HistoryStack 自体は state に持てないのでミラー）。 */
  historyCanUndo: boolean
  historyCanRedo: boolean

  /**
   * Segment ids whose Section was set by hand via SegmentInspector's
   * "セクション" select (task spec: manual-override "手動" badge). Ephemeral,
   * web-only UI state — NOT part of Composition (shared/src/types.ts's
   * Segment has no such field) — reset whenever a fresh Composition is
   * loaded (`openProject`) or `自動編集` regenerates every Segment id from
   * scratch (`runAutoEdit`), since a stale entry can never match a new id
   * anyway.
   */
  manualSectionOverrideIds: Record<string, true>

  playback: { currentSec: number; isPlaying: boolean }

  // actions
  loadProjects: () => Promise<void>
  createProject: (name: string) => Promise<void>
  /**
   * `compositionId`: optional deep-link target (docs/COMPOSER_CONTRACT.md
   * §6.1 "web: URL クエリ ?project=&composition= でそのプロジェクト・構成案を
   * 開く") — when given and found among this project's own Compositions, it
   * is opened instead of `detail.compositions[0]`. Absent/not-found falls
   * back to the existing behavior unchanged.
   */
  openProject: (id: string, compositionId?: string) => Promise<void>
  /**
   * m10 (reviewer 9/10 差し戻し残 minor): now async — flushes any pending
   * debounced save (same as `openProject`/`duplicateComposition`/
   * `createComposition`) before leaving the editor. Used to only cancel the
   * timer outright (B-1 と同型: 編集後1.5s以内に「一覧へ戻る」を押すと編集が
   * 保存されずに消える). Callers that don't need to await it (Header's
   * back-arrow button) fire it with `void backToProjects()`.
   */
  backToProjects: () => Promise<void>

  uploadFiles: (files: File[]) => Promise<void>
  toggleAssetExpanded: (assetId: string) => void

  setActiveTab: (tab: ControlTab) => void
  /** 単一選択への置き換え（既存呼び出し元と同じ意味: null か 1 件で全置換）。 */
  select: (sel: Selection) => void
  /** Shift+クリック用: 対象が選択済みなら外す、未選択なら追加する。 */
  toggleSelection: (item: SelectionItem) => void
  clearSelection: () => void

  setZoomPxPerSec: (px: number | null) => void
  /** Timeline コンポーネントが「今実際に使っている px/sec」を報告する（fit 解決後の値含む）。 */
  setEffectivePxPerSec: (px: number) => void
  zoomIn: () => void
  zoomOut: () => void
  resetZoom: () => void
  toggleSnap: () => void
  setTimelineNotice: (msg: string | null) => void
  setSnapGuideSec: (sec: number | null) => void

  undo: () => void
  redo: () => void
  beginHistoryBatch: () => void
  endHistoryBatch: () => void

  /** ⌘B: 選択 Segment（無ければ再生ヘッド下の Segment）を再生ヘッド位置で 2 分割し、右側を選択する。 */
  splitAtPlayhead: () => void
  /** Q/W: 分割して片側を捨てる（リップルは Segment 配列の並びから自動的に生じる）。 */
  splitAndKeep: (deleteSide: 'left' | 'right') => void
  /** ⌘D: 選択中の Segment/Overlay を複製する。 */
  duplicateSelected: () => void
  /** Delete/Backspace: 複数選択をまとめて 1 Undo エントリで削除する。 */
  removeSelected: () => void

  setTemplate: (t: TemplateId) => void
  setSeed: (n: number) => void
  setMeta: (patch: Partial<CompositionMeta>) => void
  /**
   * P10-3 (2026-09-06, 担当B「出力サイズ切替」): TabExport の出力サイズ select
   * が呼ぶ — `mutate()` 経由（履歴1エントリ・touch・debounce保存は既存経路と
   * 同じ）。shared/src/outputPresets.ts の OUTPUT_PRESETS から選んだプリセット
   * の { width, height, fps } をそのまま渡す。
   */
  setOutput: (output: OutputSpec) => void
  /**
   * REVIEW_v1.md M7: merges into `composition.autoEdit`, persisted with the
   * rest of the Composition — the "型と尺" tab's target duration/rhythm/
   * includeLogo controls write here instead of local `useState` (which
   * reset to hardcoded defaults on every reload).
   */
  updateAutoEditSettings: (patch: Partial<AutoEditSettings>) => void

  addSection: (s: Omit<Section, 'id'>) => void
  updateSection: (id: string, patch: Partial<Section>) => void
  removeSection: (id: string) => void
  reorderSections: (fromIdx: number, toIdx: number) => void

  addSegmentFromScene: (assetId: string, scene: Scene, insertAtIdx?: number) => void
  updateSegment: (id: string, patch: Partial<Segment>) => void
  /**
   * Same effect as `updateSegment(id, { sectionId })`, but ALSO flags the
   * Segment in `manualSectionOverrideIds` (task spec: "上書きしたSegmentには
   * 「手動」バッジ"). A separate action rather than detecting `sectionId` in
   * `updateSegment`'s generic patch so unrelated field edits (speed/cropX/
   * fit/transitionIn) never trip the badge.
   */
  setSegmentSection: (id: string, sectionId: string | null) => void
  removeSegment: (id: string) => void
  reorderSegments: (fromIdx: number, toIdx: number) => void

  addOverlay: (o: Overlay) => void
  updateOverlay: (id: string, patch: Partial<Overlay>) => void
  removeOverlay: (id: string) => void

  addAudio: (a: AudioClip) => void
  updateAudio: (id: string, patch: Partial<AudioClip>) => void
  removeAudio: (id: string) => void

  runAutoEdit: (params: AutoEditParams) => Promise<void>
  dismissAutoEditWarnings: () => void
  dismissDeepLinkNotice: () => void

  // ---- P10-2: プロジェクト/構成案の管理 ----
  /** `DELETE /api/projects/:id`. Throws (e.g. `.status === 409` while busy) — callers (ProjectList's ConfirmDialog) branch on that; only removes the row from `projects` on success. */
  deleteProject: (id: string) => Promise<void>
  /** `PATCH /api/projects/:id`. Also updates `currentProject` in place when it's the one being renamed. */
  renameProject: (id: string, name: string) => Promise<Project>
  /** `DELETE /api/compositions/:id`. Throws on 409/404 same as deleteProject; on success drops the id from `currentProject.compositionIds` (caller decides what to open next — see Header's switcher). */
  deleteComposition: (id: string) => Promise<void>
  /**
   * `POST /api/compositions/:id/duplicate`. Appends the new id to
   * `currentProject.compositionIds`; does NOT switch to it. `undefined`
   * (m9, reviewer 9/10 差し戻し残 minor) means the leading `flushPendingSave`
   * failed — nothing was duplicated, the outgoing edit's error is still
   * showing.
   */
  duplicateComposition: (id: string, name?: string) => Promise<Composition | undefined>
  /** New empty Composition (existing `makeEmptyComposition`), saved, appended to `currentProject.compositionIds`, then opened (task spec §3-9 Header switcher's「新規（空）」). */
  createComposition: (name: string) => Promise<void>
  /**
   * Not in task spec §3-10's literal 5-action list, but §3-9's switcher UI
   * itself asks for a「名前を変更」action on the CURRENTLY open Composition —
   * there is no dedicated rename endpoint (`Composition.name` is just a
   * field saved through the existing `POST /api/compositions`), so this is a
   * thin wrapper over the same `mutate()` every other Composition-field
   * setter above (`setMeta`, `setTemplate`, ...) already uses — same Undo/
   * autosave behavior, nothing new.
   */
  renameComposition: (name: string) => void

  // ---- P10-2 (docs/P10_PHASE2.md §2, 担当A: 素材の取り違え修正＋素材ビン管理) ----
  /**
   * `PATCH /api/assets/:id { label }`. Empty string removes the label
   * (display falls back to `filename` via `web/src/lib/assetName.ts`).
   * Not caught here (same convention as `deleteProject` above) — AssetCard's
   * rename input decides what to do on a thrown 400/404 itself.
   */
  renameAsset: (id: string, label: string) => Promise<void>
  /**
   * `DELETE /api/assets/:id`. Deliberately NOT caught (same convention as
   * `deleteProject`/`deleteComposition`) — AssetCard's ConfirmDialog needs
   * the raw thrown Error's `.status === 409` (`asset_in_use`/`asset_busy`)
   * to keep itself open and show the conflict instead of closing. On
   * success removes the asset from `assets`/`assetOrder`, drops its id from
   * `currentProject.assetIds`, and clears any expand/ingest-job bookkeeping
   * for it (there is nothing left to poll).
   */
  deleteAsset: (id: string) => Promise<void>
  /**
   * `POST /api/assets/:id/redetect { threshold }`. Reuses `ingestJobs` (not
   * a separate map) to track the created Job — `AssetCard.tsx`'s existing
   * ingest-progress overlay already just filters `ingestJobs` by `assetId`
   * + running/queued status regardless of `Job.kind`, so a redetect Job
   * shows the same "解析中 NN%" overlay for free (task spec §2 item 4:
   * "進捗（既存の ingest 進捗表示を流用）"). Once done, re-fetches the Asset so
   * the updated `scenes` (and their SceneChip thumbnails) replace the old
   * ones.
   */
  redetectAsset: (id: string, threshold: number) => Promise<void>

  scheduleSave: () => void
  saveNow: () => Promise<void>

  requestPreview: () => Promise<void>
  requestRender: () => Promise<void>

  setPlayhead: (sec: number) => void
  setPlaying: (playing: boolean) => void
  togglePlaying: () => void

  /**
   * P10-2 担当B (`composition.sourceAudio`「素材の元音声を残す（環境音）」,
   * task spec §3 item 6): merges into `composition.sourceAudio` (absent ->
   * `{enabled:false, gainDb:-12}` per shared/src/types.ts's own doc
   * comment) via the same `mutate()` every other Composition-field setter
   * above uses — one Undo history entry, same autosave debounce. No
   * dedicated `updateComposition` action existed to reuse (task spec's own
   * fallback instruction), so this is a new one, appended at the end of
   * this interface/store per the shared-file (editorStore.ts) append-only
   * rule.
   */
  setSourceAudio: (patch: Partial<{ enabled: boolean; gainDb: number }>) => void
}

let saveTimer: ReturnType<typeof setTimeout> | null = null
const SAVE_DEBOUNCE_MS = 1500

/** Undo/Redo スタック（Composition スナップショット・上限 100）。task spec §2-12。 */
const history = new HistoryStack<Composition>(100)
let noticeTimer: ReturnType<typeof setTimeout> | null = null
const NOTICE_MS = 1800

function touch(comp: Composition): Composition {
  return { ...comp, updatedAt: new Date().toISOString() }
}

export const useEditorStore = create<EditorState>((set, get) => ({
  screen: 'projects',

  projects: [],
  projectsLoading: false,
  projectsError: null,

  currentProject: null,
  assets: {},
  assetOrder: [],
  composition: null,
  editorLoading: false,
  editorError: null,

  saveState: 'idle',
  saveError: null,

  uploads: {},

  ingestJobs: {},
  autoEditRunning: false,
  autoEditError: null,
  autoEditWarnings: [],
  deepLinkNotice: null,

  previewJob: null,
  previewUrl: null,
  renderJob: null,
  renderHistory: [],

  selection: null,
  selectedIds: [],
  activeTab: 'template',
  expandedAssetIds: {},
  manualSectionOverrideIds: {},

  zoomPxPerSec: null,
  effectivePxPerSec: 50,
  snapEnabled: true,
  timelineNotice: null,
  snapGuideSec: null,
  historyCanUndo: false,
  historyCanRedo: false,

  playback: { currentSec: 0, isPlaying: false },

  async loadProjects() {
    set({ projectsLoading: true, projectsError: null })
    try {
      const projects = await api.listProjects()
      set({ projects, projectsLoading: false })
    } catch (e) {
      set({ projectsError: String((e as Error).message ?? e), projectsLoading: false })
    }
  },

  async createProject(name) {
    set({ projectsError: null })
    try {
      const p = await api.createProject(name)
      set((s) => ({ projects: [...s.projects, p] }))
      await get().openProject(p.id)
    } catch (e) {
      set({ projectsError: String((e as Error).message ?? e) })
    }
  },

  async openProject(id, compositionId) {
    // MAJOR B-1 (reviewer 7/10 差し戻し): flush BEFORE anything else in this
    // action — including before the `editorLoading`/`screen` `set()` right
    // below — so `flushPendingSave`'s own `saveNow()` still reads
    // `get().composition` as the OUTGOING Composition, not whatever this
    // call is about to replace it with. Previously nothing cancelled
    // `scheduleSave`'s debounce timer here (only `backToProjects` did): the
    // timer kept running across a switch, and when it eventually fired,
    // `saveNow` read `get().composition` AFTER the switch had already
    // replaced it — the outgoing Composition's pending edit was silently
    // dropped, and the Composition just switched TO got a pointless
    // version+1 POST. (P10 前は編集画面内で openProject を呼ぶ経路が無く潜在
    // していた — Header の構成案スイッチャーで初めて踏める。)
    //
    // m9 (reviewer 9/10 差し戻し残 minor): if that flush's `saveNow()`
    // itself fails, `flushPendingSave` returns false — abort the switch
    // entirely rather than proceeding to reset `saveState` to 'saved' a few
    // lines below, which would have silently painted over the error (the
    // dropped edit would then look successfully saved instead of failed).
    // The outgoing Composition/its `saveState:'error'` stay exactly as they
    // are so the existing error UI (Header's `save-state` pill) keeps
    // showing it.
    if (!(await flushPendingSave(set, get))) return
    // Bug #1 (小林 report 2026-09-05): `playback` (currentSec/isPlaying) lives
    // in this store, NOT in usePreviewPlayback's component-local refs — a
    // previous project's RAF loop is cancelled when EditorScreen unmounts
    // (screen leaves 'editor'), but that only stops advancing THIS render's
    // state, it never resets it. Without this, opening/creating a new
    // project resumed with the old project's `isPlaying: true` and stale
    // `currentSec` still in the store, so the instant EditorScreen/
    // PreviewPanel remounted for the NEW project, usePreviewPlayback's RAF
    // effect saw `isPlaying: true` on its very first run and immediately
    // started auto-advancing the NEW project's playhead/video from that
    // leftover offset — looking exactly like "the previous project's
    // timebar is still running" on the new one. Reset here (not only in
    // `backToProjects`) so every path that lands on a Composition — fresh
    // load, `createProject`'s internal call, or a retry from the error
    // state — starts paused at 0.
    set({ editorLoading: true, editorError: null, screen: 'editor', playback: { currentSec: 0, isPlaying: false } })
    try {
      const detail = await api.getProject(id)
      const assets: Record<string, Asset> = {}
      for (const a of detail.assets) assets[a.id] = a
      // Deep link (docs/COMPOSER_CONTRACT.md §6.1): prefer the requested
      // compositionId when it actually belongs to this project; an
      // unknown/stale id (e.g. a Composition since deleted) falls back to
      // the existing default instead of erroring the whole screen out.
      const requested = compositionId ? detail.compositions.find((c) => c.id === compositionId) : undefined
      // reviewer 6/10 m2: a compositionId that doesn't resolve is no longer
      // a silent fallback — surfaced via JobToast's own notice panel.
      const deepLinkNotice =
        compositionId && !requested ? '指定の構成案が見つかりません。先頭の構成案を開きます。' : null
      let composition = requested ?? detail.compositions[0] ?? null
      if (!composition) {
        composition = makeEmptyComposition(detail.project.id, '初稿')
        composition = await api.saveComposition(composition)
      }
      // REVIEW_v1.md M6: past exports used to live only in this store's
      // in-memory renderHistory (requestRender's onDone push below), so
      // they vanished on reload even though the server still had the
      // completed render Job + file. Hydrate from GET /api/jobs instead —
      // best-effort: a failure here must not block opening the project,
      // it just leaves the export tab's history empty (same as before this
      // fix), not lie about it.
      //
      // P10-3 担当A (docs/P10_PHASE3.md §2 items 5/6): keeps the raw Job[]
      // now (was mapped down to `{jobId,resultPath,createdAt}`) — no longer
      // filtered to `status==='done'` either: ExportHistory.tsx shows an
      // 'error' row too (✕ + the error text, no download), so dropping it
      // here would silently hide it. `Job.updatedAt` descending is already
      // `listJobs`'s own sort order server-side (mockClient.ts mirrors it).
      const renderHistory = await api.listJobs({ compositionId: composition.id, kind: 'render' }).catch(() => [])
      // A fresh Composition means Undo history from the PREVIOUS project (or no
      // project) refers to states this one has never seen — clearing it here
      // (not only in `backToProjects`) covers every path that lands on a
      // Composition: fresh load, `createProject`'s internal call, and a retry
      // from the error state.
      history.clear()
      set({
        currentProject: detail.project,
        assets,
        assetOrder: detail.assets.map((a) => a.id),
        composition,
        editorLoading: false,
        saveState: 'saved',
        selection: null,
        selectedIds: [],
        previewUrl: null,
        previewJob: null,
        renderJob: null,
        renderHistory,
        autoEditWarnings: [],
        autoEditError: null,
        manualSectionOverrideIds: {},
        historyCanUndo: false,
        historyCanRedo: false,
        timelineNotice: null,
        deepLinkNotice,
      })
      // m4 (reviewer 7/10 差し戻し): keep an ALREADY-deep-linked URL's
      // `?project=&composition=` in sync with whatever is actually open —
      // App.tsx only READS these once on mount (docs/COMPOSER_CONTRACT.md
      // §6.1 deep link), so without this, switching/duplicating/deleting a
      // Composition inside a tab that was opened via a deep link (e.g. a
      // ComposerDraft.studioUrl link, or this very replaceState from an
      // earlier switch) left `?composition=` pointing at whatever was open
      // when the tab FIRST loaded — reloading such a tab after switching
      // reopened the wrong (stale) Composition.
      //
      // Deliberately does nothing when the URL has no `?project=` at all
      // (task spec's own wording: "deep link の既存挙動を壊さない") — the far
      // more common path, a plain ProjectList card click, must keep
      // reloading back to ProjectList exactly as before (layout-resize.
      // spec.ts's "reload 後も保持される" test relies on this: it asserts a
      // full reload resets `screen` to 'projects', requiring a fresh click
      // to reopen — unconditionally stamping `?project=` onto every
      // `openProject` call would have silently turned every ordinary
      // session into a permanent deep link and broken that).
      if (typeof window !== 'undefined' && window.history?.replaceState) {
        const url = new URL(window.location.href)
        if (url.searchParams.has('project')) {
          url.searchParams.set('project', detail.project.id)
          url.searchParams.set('composition', composition.id)
          window.history.replaceState(null, '', url.toString())
        }
      }
    } catch (e) {
      set({ editorError: String((e as Error).message ?? e), editorLoading: false })
    }
  },

  async backToProjects() {
    // m10 (reviewer 9/10 差し戻し残 minor): FLUSH, not just cancel — this
    // used to only `clearTimeout` the pending debounce timer outright
    // (B-1と同型のバグ): renaming/editing and then clicking "一覧へ戻る"
    // within the 1.5s debounce window dropped the edit exactly like the
    // openProject case B-1 fixed, just via a different exit. m9: abort
    // (stay in the editor, error still visible) if that flush itself fails.
    if (!(await flushPendingSave(set, get))) return
    history.clear()
    // Defense in depth alongside the `openProject` reset above: stop any
    // leftover playback state the instant the editor is left, rather than
    // relying solely on the next `openProject` call to clean up after it.
    set({
      screen: 'projects',
      currentProject: null,
      composition: null,
      playback: { currentSec: 0, isPlaying: false },
      selection: null,
      selectedIds: [],
      historyCanUndo: false,
      historyCanRedo: false,
    })
  },

  async uploadFiles(files) {
    const project = get().currentProject
    if (!project) return
    const localIds = files.map(() => makeId('up'))
    set((s) => {
      const uploads = { ...s.uploads }
      files.forEach((f, i) => {
        uploads[localIds[i]] = {
          id: localIds[i],
          fileName: f.name,
          loaded: 0,
          total: f.size || 1,
          status: 'uploading',
        }
      })
      return { uploads }
    })

    try {
      const jobs = await api.uploadAssets(project.id, files, (p) => {
        set((s) => {
          const entry = Object.values(s.uploads).find((u) => u.fileName === p.fileName)
          if (!entry) return s
          return {
            uploads: { ...s.uploads, [entry.id]: { ...entry, loaded: p.loaded, total: p.total } },
          }
        })
      })

      jobs.forEach((job, i) => {
        const localId = localIds[i]
        set((s) => ({
          uploads: {
            ...s.uploads,
            [localId]: { ...s.uploads[localId], status: 'processing', assetId: job.assetId },
          },
          ingestJobs: { ...s.ingestJobs, [job.id]: job },
        }))
        // Show the Asset card immediately in its pending/processing ingestStatus
        // (DESIGN §6: "Asset カード... ingest 状態") instead of waiting for the
        // ingest Job to finish — only the thumb/scenes are still to come.
        if (job.assetId) {
          api
            .getAsset(job.assetId)
            .then((asset) => {
              set((s) => ({
                assets: { ...s.assets, [asset.id]: asset },
                assetOrder: s.assetOrder.includes(asset.id) ? s.assetOrder : [...s.assetOrder, asset.id],
              }))
            })
            .catch(() => {
              // not fatal here — the post-job getAsset call below will retry
            })
        }
        pollJob(job.id, (updated) => {
          set((s) => ({ ingestJobs: { ...s.ingestJobs, [job.id]: updated } }))
          if (updated.status === 'done' && updated.assetId) {
            api
              .getAsset(updated.assetId)
              .then((asset) => {
                set((s) => ({
                  assets: { ...s.assets, [asset.id]: asset },
                  assetOrder: s.assetOrder.includes(asset.id)
                    ? s.assetOrder
                    : [...s.assetOrder, asset.id],
                  uploads: {
                    ...s.uploads,
                    [localId]: { ...s.uploads[localId], status: 'done', loaded: s.uploads[localId]?.total ?? 1 },
                  },
                  currentProject: s.currentProject
                    ? {
                        ...s.currentProject,
                        assetIds: s.currentProject.assetIds.includes(asset.id)
                          ? s.currentProject.assetIds
                          : [...s.currentProject.assetIds, asset.id],
                      }
                    : s.currentProject,
                }))
              })
              .catch((e) => {
                set((s) => ({
                  uploads: {
                    ...s.uploads,
                    [localId]: { ...s.uploads[localId], status: 'error', error: String(e) },
                  },
                }))
              })
          } else if (updated.status === 'error') {
            set((s) => ({
              uploads: {
                ...s.uploads,
                [localId]: { ...s.uploads[localId], status: 'error', error: updated.error },
              },
            }))
          }
        })
      })
    } catch (e) {
      localIds.forEach((localId) => {
        set((s) => ({
          uploads: {
            ...s.uploads,
            [localId]: { ...s.uploads[localId], status: 'error', error: String((e as Error).message ?? e) },
          },
        }))
      })
    }
  },

  toggleAssetExpanded(assetId) {
    set((s) => ({ expandedAssetIds: { ...s.expandedAssetIds, [assetId]: !s.expandedAssetIds[assetId] } }))
  },

  setActiveTab(tab) {
    set({ activeTab: tab })
  },

  select(sel) {
    set({ selection: sel, selectedIds: sel ? [sel] : [] })
  },

  toggleSelection(item) {
    set((s) => {
      const exists = s.selectedIds.some((x) => x.kind === item.kind && x.id === item.id)
      const selectedIds = exists
        ? s.selectedIds.filter((x) => !(x.kind === item.kind && x.id === item.id))
        : [...s.selectedIds, item]
      return { selectedIds, selection: selectedIds[0] ?? null }
    })
  },

  clearSelection() {
    set({ selection: null, selectedIds: [] })
  },

  setTemplate(t) {
    mutate(set, get, (c) => ({ ...c, template: t }))
  },

  setSeed(n) {
    mutate(set, get, (c) => ({ ...c, seed: n }))
  },

  setMeta(patch) {
    mutate(set, get, (c) => ({ ...c, meta: { ...c.meta, ...patch } }))
  },

  setOutput(output) {
    mutate(set, get, (c) => ({ ...c, output }))
  },

  updateAutoEditSettings(patch) {
    mutate(set, get, (c) => ({ ...c, autoEdit: { ...c.autoEdit, ...patch } as AutoEditSettings }))
  },

  addSection(s) {
    mutate(set, get, (c) => ({ ...c, sections: [...c.sections, { ...s, id: makeId('sec') }] }))
  },

  updateSection(id, patch) {
    mutate(set, get, (c) => ({
      ...c,
      sections: c.sections.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    }))
  },

  removeSection(id) {
    mutate(set, get, (c) => ({
      ...c,
      sections: c.sections.filter((s) => s.id !== id),
      tracks: {
        ...c.tracks,
        video: c.tracks.video.map((seg) => (seg.sectionId === id ? { ...seg, sectionId: null } : seg)),
      },
    }))
  },

  reorderSections(fromIdx, toIdx) {
    mutate(set, get, (c) => {
      const arr = [...c.sections]
      const [moved] = arr.splice(fromIdx, 1)
      arr.splice(toIdx, 0, moved)
      return { ...c, sections: arr }
    })
  },

  addSegmentFromScene(assetId, scene, insertAtIdx) {
    mutate(set, get, (c) => {
      const seg: Segment = {
        id: makeId('seg'),
        assetId,
        inSec: scene.startSec,
        outSec: scene.endSec,
        speed: 1,
        sectionId: c.sections[0]?.id ?? null,
        fit: 'cover',
        cropX: 0.5,
        transitionIn: c.tracks.video.length === 0 ? 'cut' : 'fade',
      }
      const video = [...c.tracks.video]
      if (insertAtIdx == null || insertAtIdx >= video.length) video.push(seg)
      else video.splice(insertAtIdx, 0, seg)
      return { ...c, tracks: { ...c.tracks, video } }
    })
  },

  updateSegment(id, patch) {
    mutate(set, get, (c) => ({
      ...c,
      tracks: {
        ...c.tracks,
        video: c.tracks.video.map((s) => (s.id === id ? { ...s, ...patch } : s)),
      },
    }))
  },

  setSegmentSection(id, sectionId) {
    mutate(set, get, (c) => ({
      ...c,
      tracks: {
        ...c.tracks,
        video: c.tracks.video.map((s) => (s.id === id ? { ...s, sectionId } : s)),
      },
    }))
    set((s) => ({ manualSectionOverrideIds: { ...s.manualSectionOverrideIds, [id]: true } }))
  },

  removeSegment(id) {
    mutate(set, get, (c) => ({
      ...c,
      tracks: { ...c.tracks, video: c.tracks.video.filter((s) => s.id !== id) },
    }))
    deselectItem(set, get, 'segment', id)
  },

  reorderSegments(fromIdx, toIdx) {
    mutate(set, get, (c) => {
      const arr = [...c.tracks.video]
      const [moved] = arr.splice(fromIdx, 1)
      arr.splice(toIdx, 0, moved)
      return { ...c, tracks: { ...c.tracks, video: arr } }
    })
  },

  addOverlay(o) {
    mutate(set, get, (c) => ({ ...c, tracks: { ...c.tracks, overlays: [...c.tracks.overlays, o] } }))
  },

  updateOverlay(id, patch) {
    mutate(set, get, (c) => ({
      ...c,
      tracks: {
        ...c.tracks,
        overlays: c.tracks.overlays.map((o) => (o.id === id ? ({ ...o, ...patch } as Overlay) : o)),
      },
    }))
  },

  removeOverlay(id) {
    mutate(set, get, (c) => ({
      ...c,
      tracks: { ...c.tracks, overlays: c.tracks.overlays.filter((o) => o.id !== id) },
    }))
    deselectItem(set, get, 'overlay', id)
  },

  addAudio(a) {
    mutate(set, get, (c) => ({ ...c, tracks: { ...c.tracks, audio: [...c.tracks.audio, a] } }))
  },

  updateAudio(id, patch) {
    mutate(set, get, (c) => ({
      ...c,
      tracks: { ...c.tracks, audio: c.tracks.audio.map((a) => (a.id === id ? { ...a, ...patch } : a)) },
    }))
  },

  removeAudio(id) {
    mutate(set, get, (c) => ({
      ...c,
      tracks: { ...c.tracks, audio: c.tracks.audio.filter((a) => a.id !== id) },
    }))
    deselectItem(set, get, 'audio', id)
  },

  async runAutoEdit(params) {
    const comp = get().composition
    if (!comp) return
    set({ autoEditRunning: true, autoEditError: null })
    try {
      // {composition, warnings, stats} — server/src/routes/compositions.ts
      // POST /:id/auto response (task spec S5 item 1). warnings are engine
      // diagnostics (e.g. footage shortage) and must never be dropped
      // silently (CLAUDE.md "サイレント禁止") — surfaced via autoEditWarnings
      // (JobToast toast + a persistent panel in TabTemplate).
      const { composition: result, warnings, stats } = await api.autoEdit(comp.id, params)
      // TRIAL_REPORT_v5.md item 6: append 'hue' mode's per-Section hue-match
      // breakdown to the SAME warnings panel as informational lines (not a
      // new UI element) — only Sections that actually scored a chromatic
      // match (meanDeg !== null); sequential mode / no chromatic data yields
      // stats.perSection === [] and this is a no-op.
      const perSectionLines = stats.perSection
        .filter((p) => p.meanDeg !== null)
        .map((p) => `${p.name || '(無名の章)'}: 平均${p.meanDeg!.toFixed(1)}°`)
      // task spec §2-12 "自動編集の実行も1エントリ": this doesn't go through
      // `mutate()` (the composition here isn't derived from `comp` by a simple
      // transform — it's a wholesale server result), so push the pre-run
      // snapshot by hand — one Undo after 自動編集 restores exactly the
      // Composition that was live right before this ran.
      history.push(comp)
      set({
        composition: touch({ ...comp, ...result, id: comp.id, projectId: comp.projectId }),
        autoEditRunning: false,
        autoEditWarnings: [...warnings, ...perSectionLines],
        selection: null,
        selectedIds: [],
        historyCanUndo: history.canUndo(),
        historyCanRedo: history.canRedo(),
        // every Segment id is freshly regenerated by this run — any prior
        // manual "セクション" override necessarily no longer matches a live
        // Segment, so drop it (task spec: "自動編集を再実行すると上書きは消える").
        manualSectionOverrideIds: {},
        // Bug (found while investigating 小林's timeline/timebar report,
        // 2026-09-05): `playback.currentSec` used to survive this swap
        // untouched — re-running 自動編集 with a shorter `targetDurationSec`
        // than the OLD playhead position left `currentSec` pointing past the
        // brand-new (shorter) Composition's real end (DOM-verified: scrubbed
        // to 0:28 on a 29s cut, re-ran 自動編集 targeting a new ~11.7s result,
        // current-time stayed frozen at "0:28" — 2.5x past the new
        // total-time — and the playhead marker rendered at 1400px, far past
        // the new, much narrower timeline). Every Segment/Overlay id is
        // already thrown away and rebuilt above (see the id comment right
        // below) — the old playhead position is just as stale as those ids,
        // so reset it the same way.
        playback: { currentSec: 0, isPlaying: false },
      })
      get().scheduleSave()
    } catch (e) {
      set({ autoEditRunning: false, autoEditError: String((e as Error).message ?? e) })
    }
  },

  dismissAutoEditWarnings() {
    set({ autoEditWarnings: [] })
  },

  dismissDeepLinkNotice() {
    set({ deepLinkNotice: null })
  },

  scheduleSave() {
    set({ saveState: 'saving' })
    if (saveTimer) clearTimeout(saveTimer)
    saveTimer = setTimeout(() => {
      get().saveNow()
    }, SAVE_DEBOUNCE_MS)
  },

  async saveNow() {
    const comp = get().composition
    if (!comp) return
    set({ saveState: 'saving' })
    try {
      const saved = await api.saveComposition(comp)
      set({ composition: saved, saveState: 'saved', saveError: null })
    } catch (e) {
      set({ saveState: 'error', saveError: String((e as Error).message ?? e) })
    }
  },

  async requestPreview() {
    const comp = get().composition
    if (!comp) return
    await get().saveNow()
    try {
      const job = await api.createJob({ kind: 'preview', compositionId: comp.id })
      set({ previewJob: job, previewUrl: null })
      pollJob(job.id, (updated) => {
        set({ previewJob: updated })
        if (updated.status === 'done' && updated.resultPath) {
          set({ previewUrl: api.fileUrl(updated.resultPath) })
        }
      })
    } catch (e) {
      set({
        previewJob: {
          id: 'error',
          kind: 'preview',
          status: 'error',
          progress: 0,
          log: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          error: String((e as Error).message ?? e),
        },
      })
    }
  },

  async requestRender() {
    const comp = get().composition
    if (!comp) return
    await get().saveNow()
    try {
      const job = await api.createJob({ kind: 'render', compositionId: comp.id })
      set({ renderJob: job })
      pollJob(job.id, (updated) => {
        set({ renderJob: updated })
        if (updated.status === 'done' && updated.resultPath) {
          // P10-3 担当A (docs/P10_PHASE3.md §2 item 5): refetch via the API
          // instead of hand-building a history entry from `updated` — keeps
          // `renderHistory` a real `Job[]` (size/尺/容量/result come from
          // THIS refetch, not from guessing at the shape here) straight from
          // the same source `openProject`'s hydrate above uses.
          api
            .listJobs({ compositionId: comp.id, kind: 'render' })
            .then((renderHistory) => {
              // reviewer minor A-8: `comp` is the Composition this render
              // was STARTED against — pollJob's callback can fire well
              // after the user has since switched to a different
              // Composition (openProject replaces `get().composition`
              // wholesale). Without this guard, a slow render finishing
              // after such a switch would overwrite the NOW-open
              // Composition's own `renderHistory` with the old one's list.
              if (get().composition?.id === comp.id) set({ renderHistory })
            })
            .catch(() => {
              // best-effort — a failed refetch just leaves the export tab's
              // list one render behind, same fallback posture as the hydrate
              // in `openProject` above.
            })
        }
      })
    } catch (e) {
      set({
        renderJob: {
          id: 'error',
          kind: 'render',
          status: 'error',
          progress: 0,
          log: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          error: String((e as Error).message ?? e),
        },
      })
    }
  },

  setPlayhead(sec) {
    // Bug (found while investigating the 小林 playback-leak report, 2026-09-05):
    // this had no UPPER bound — only `Math.max(0, sec)` — so every caller that
    // computes `sec` from something other than the RAF loop (which already
    // guards itself against `totalSec`) could push `currentSec` past the
    // Composition's real end with nothing to stop it: Timeline.tsx's ruler
    // click has no clamp (`scrubFromClientX`'s dead zone past the last tick,
    // Ruler.tsx's own `+5` margin — DOM-verified: clicking there set
    // current-time to 0:34 while total-time read 0:29), and
    // useKeyboardShortcuts.ts's ArrowRight nudge has no ceiling either (each
    // press just keeps adding FRAME_SEC forever). Centralized here (not
    // patched at each call site) so every current AND future caller of
    // `setPlayhead` gets the same guarantee for free.
    set((s) => {
      const total = s.composition ? getTotalDurationSec(s.composition) : 0
      const clamped = total > 0 ? Math.min(Math.max(0, sec), total) : 0
      return { playback: { ...s.playback, currentSec: clamped } }
    })
  },

  setPlaying(playing) {
    set((s) => ({ playback: { ...s.playback, isPlaying: playing } }))
  },

  togglePlaying() {
    set((s) => ({ playback: { ...s.playback, isPlaying: !s.playback.isPlaying } }))
  },

  // ---- UI MK2 第1段: ズーム/吸着 ----
  setZoomPxPerSec(px) {
    set({ zoomPxPerSec: px == null ? null : clampPxPerSec(px) })
  },

  setEffectivePxPerSec(px) {
    set({ effectivePxPerSec: px })
  },

  zoomIn() {
    const cur = get().effectivePxPerSec
    get().setZoomPxPerSec(clampPxPerSec(cur * 1.4))
  },

  zoomOut() {
    const cur = get().effectivePxPerSec
    get().setZoomPxPerSec(clampPxPerSec(cur / 1.4))
  },

  resetZoom() {
    // null = 「全体を表示」(fit) に戻す
    set({ zoomPxPerSec: null })
  },

  toggleSnap() {
    set((s) => ({ snapEnabled: !s.snapEnabled }))
  },

  setTimelineNotice(msg) {
    set({ timelineNotice: msg })
    if (noticeTimer) {
      clearTimeout(noticeTimer)
      noticeTimer = null
    }
    if (msg) {
      noticeTimer = setTimeout(() => set({ timelineNotice: null }), NOTICE_MS)
    }
  },

  setSnapGuideSec(sec) {
    set({ snapGuideSec: sec })
  },

  // ---- UI MK2 第1段: Undo/Redo ----
  undo() {
    const comp = get().composition
    if (!comp) return
    const prev = history.undo(comp)
    if (prev === undefined) return
    // reviewer 差し戻し M1: 巻き戻した先に存在しない id を選択に残すと右ペインが
    // 空白の Segment インスペクタのまま固まる — 新しい Composition に実在する
    // id だけへ filter する（selection ミラーも同時に更新）。
    const selectedIds = pruneSelection(prev, get().selectedIds)
    set({
      composition: prev,
      historyCanUndo: history.canUndo(),
      historyCanRedo: history.canRedo(),
      selectedIds,
      selection: selectedIds[0] ?? null,
    })
    get().scheduleSave()
    // 尺が短くなった Composition に戻った時、再生ヘッドが末尾を超えたままに
    // ならないよう再クランプ（setPlayhead は現在の composition 基準で clamp する）。
    get().setPlayhead(get().playback.currentSec)
  },

  redo() {
    const comp = get().composition
    if (!comp) return
    const next = history.redo(comp)
    if (next === undefined) return
    const selectedIds = pruneSelection(next, get().selectedIds)
    set({
      composition: next,
      historyCanUndo: history.canUndo(),
      historyCanRedo: history.canRedo(),
      selectedIds,
      selection: selectedIds[0] ?? null,
    })
    get().scheduleSave()
    get().setPlayhead(get().playback.currentSec)
  },

  beginHistoryBatch() {
    history.beginBatch()
  },

  endHistoryBatch() {
    history.endBatch()
  },

  // ---- UI MK2 第1段: 分割 / 複製 / 一括削除 ----
  splitAtPlayhead() {
    const comp = get().composition
    if (!comp) return
    const sec = get().playback.currentSec
    const timeline = getSegmentTimeline(comp.tracks.video)
    const selId = get().selectedIds.find((s) => s.kind === 'segment')?.id
    // reviewer 差し戻し m4: 選択 Segment があっても再生ヘッドがその範囲外なら
    // 「再生ヘッド下の Segment」へフォールバックする（CapCut と同様）。
    const timed = resolveSplitTarget(timeline, selId, sec)
    if (!timed) {
      get().setTimelineNotice('分割できる Segment がありません')
      return
    }
    const split = computeSplit(timed, sec)
    if (!split) {
      get().setTimelineNotice('端では分割できません')
      return
    }
    const rightId = makeId('seg')
    mutate(set, get, (c) => {
      const video = [...c.tracks.video]
      const idx = video.findIndex((s) => s.id === timed.segment.id)
      if (idx === -1) return c
      // reviewer 差し戻し B2: buildSplitSegments が右側の transitionIn を必ず
      // 'cut' にする（元が 'fade' でも継承しない — 継承すると連続ショットの
      // 途中に意図しないフェードが入り書き出しが破綻する）。
      const { left, right } = buildSplitSegments(timed.segment, split, rightId)
      video.splice(idx, 1, left, right)
      return { ...c, tracks: { ...c.tracks, video } }
    })
    get().select({ kind: 'segment', id: rightId })
  },

  splitAndKeep(deleteSide) {
    const comp = get().composition
    if (!comp) return
    const sec = get().playback.currentSec
    const timeline = getSegmentTimeline(comp.tracks.video)
    const selId = get().selectedIds.find((s) => s.kind === 'segment')?.id
    const timed = resolveSplitTarget(timeline, selId, sec)
    if (!timed) {
      get().setTimelineNotice('分割できる Segment がありません')
      return
    }
    const split = computeSplit(timed, sec)
    if (!split) {
      get().setTimelineNotice('端では分割できません')
      return
    }
    mutate(set, get, (c) => ({
      ...c,
      tracks: {
        ...c.tracks,
        video: c.tracks.video.map((s) =>
          s.id !== timed.segment.id
            ? s
            : deleteSide === 'left'
              ? { ...s, inSec: split.rightInSec } // 左を捨てる = 右側だけ残す
              : { ...s, outSec: split.leftOutSec }, // 右を捨てる = 左側だけ残す
        ),
      },
    }))
  },

  duplicateSelected() {
    const comp = get().composition
    if (!comp) return
    const sel = get().selectedIds[0]
    if (!sel) return
    if (sel.kind === 'segment') {
      const idx = comp.tracks.video.findIndex((s) => s.id === sel.id)
      if (idx === -1) return
      const copyId = makeId('seg')
      mutate(set, get, (c) => {
        const video = [...c.tracks.video]
        const i = video.findIndex((s) => s.id === sel.id)
        if (i === -1) return c
        video.splice(i + 1, 0, { ...video[i], id: copyId })
        return { ...c, tracks: { ...c.tracks, video } }
      })
      get().select({ kind: 'segment', id: copyId })
    } else if (sel.kind === 'overlay') {
      const original = comp.tracks.overlays.find((o) => o.id === sel.id)
      if (!original || original.kind === 'logo') return
      const copyId = makeId('ov')
      mutate(set, get, (c) => ({
        ...c,
        tracks: {
          ...c.tracks,
          overlays: [
            ...c.tracks.overlays,
            { ...original, id: copyId, startSec: original.startSec + original.durationSec } as Overlay,
          ],
        },
      }))
      get().select({ kind: 'overlay', id: copyId })
    }
    // audio クリップの複製は第1段の対象外（task spec §2-6 は Segment/Overlay のみ）
  },

  removeSelected() {
    const items = get().selectedIds
    if (items.length === 0) return
    get().beginHistoryBatch()
    // reviewer 差し戻し m1: どれか1件の remove* が例外を投げても endHistoryBatch
    // が必ず呼ばれるようにする — バッチが開いたままだと以後の push が全部
    // 無視され、Undo が静かに壊れる。
    try {
      for (const item of items) {
        if (item.kind === 'segment') get().removeSegment(item.id)
        else if (item.kind === 'overlay') get().removeOverlay(item.id)
        else if (item.kind === 'audio') get().removeAudio(item.id)
      }
    } finally {
      get().endHistoryBatch()
    }
  },

  // ---- P10-2: プロジェクト/構成案の管理 ----
  async deleteProject(id) {
    // Deliberately NOT caught here (unlike loadProjects/createProject above)
    // — ProjectList's ConfirmDialog needs the raw thrown Error (its
    // `.status`) to tell a 409 (keep dialog open, show message) apart from
    // success (close it), which a store-side catch-and-store-as-string
    // would erase.
    await api.deleteProject(id)
    set((s) => ({ projects: s.projects.filter((p) => p.id !== id) }))
  },

  async renameProject(id, name) {
    const updated = await api.renameProject(id, name)
    set((s) => ({
      projects: s.projects.map((p) => (p.id === id ? updated : p)),
      currentProject: s.currentProject?.id === id ? updated : s.currentProject,
    }))
    return updated
  },

  async deleteComposition(id) {
    // MAJOR B-1 (reviewer 7/10 差し戻し): deliberately CANCEL the pending
    // debounce timer here, never flush/save it. Deleting is normally called
    // on the Composition currently open (Header's switcher), which is
    // exactly what any pending timer would be about to save — letting that
    // POST land AFTER this DELETE has already removed the Composition JSON
    // means `routes/compositions.ts`'s POST finds no `existing` and mints a
    // BRAND NEW Composition via `genId('comp')`: a zombie the delete was
    // supposed to prevent from ever existing. Any pending edit for a
    // Composition about to be deleted is moot — there's nothing worth
    // saving it TO.
    if (saveTimer) {
      clearTimeout(saveTimer)
      saveTimer = null
    }
    await api.deleteComposition(id)
    set((s) => ({
      currentProject: s.currentProject
        ? { ...s.currentProject, compositionIds: s.currentProject.compositionIds.filter((cid) => cid !== id) }
        : s.currentProject,
    }))
  },

  async duplicateComposition(id, name) {
    // MAJOR B-1 (reviewer 7/10 差し戻し): flush first — `duplicateComposition`
    // is always called with the CURRENTLY open Composition's id (Header's
    // switcher), so an unsaved debounced edit sitting in `saveTimer` would
    // otherwise be invisible to the server-side duplicate (it reads
    // whatever's already persisted), silently dropping the edit from the
    // copy too ("複製が直前の編集を含まない").
    // m9 (reviewer 9/10 差し戻し残 minor): bail out (no duplicate created) if
    // that flush itself failed — don't duplicate a Composition whose most
    // recent edit is known to be unsaved/lost, and don't let this action
    // touch `saveState` on the way past it.
    if (!(await flushPendingSave(set, get))) return undefined
    const duplicate = await api.duplicateComposition(id, name)
    set((s) => ({
      currentProject: s.currentProject
        ? { ...s.currentProject, compositionIds: [...s.currentProject.compositionIds, duplicate.id] }
        : s.currentProject,
    }))
    return duplicate
  },

  async createComposition(name) {
    // MAJOR B-1 (reviewer 7/10 差し戻し): flush before doing anything else —
    // this ends with `openProject`, which flushes again too (harmless no-op
    // the second time since this call already cleared `saveTimer`), but
    // flushing here first keeps the ordering explicit: save the outgoing
    // Composition's pending edit BEFORE creating a new one, not as a side
    // effect of the openProject call buried at the end of this action.
    // m9 (reviewer 9/10 差し戻し残 minor): bail out if that flush failed —
    // don't create (and switch into) a new Composition while the outgoing
    // one's edit is known unsaved/lost.
    if (!(await flushPendingSave(set, get))) return
    const project = get().currentProject
    if (!project) return
    const draft = makeEmptyComposition(project.id, name)
    const saved = await api.saveComposition(draft)
    set((s) => ({
      currentProject: s.currentProject
        ? { ...s.currentProject, compositionIds: [...s.currentProject.compositionIds, saved.id] }
        : s.currentProject,
    }))
    await get().openProject(project.id, saved.id)
  },

  renameComposition(name) {
    mutate(set, get, (c) => ({ ...c, name }))
  },

  setSourceAudio(patch) {
    mutate(set, get, (c) => ({
      ...c,
      sourceAudio: { enabled: false, gainDb: -12, ...c.sourceAudio, ...patch },
    }))
  },

  // ---- P10-2 (docs/P10_PHASE2.md §2, 担当A: 素材の取り違え修正＋素材ビン管理) ----
  async renameAsset(id, label) {
    const updated = await api.patchAssetLabel(id, label)
    set((s) => ({ assets: { ...s.assets, [id]: updated } }))
  },

  async deleteAsset(id) {
    // Deliberately NOT caught (same convention as `deleteProject` above) —
    // AssetCard's ConfirmDialog needs the raw thrown Error's `.status===409`
    // to keep itself open and show the conflict instead of closing.
    await api.deleteAsset(id)
    set((s) => {
      const nextAssets = { ...s.assets }
      delete nextAssets[id]
      const nextExpanded = { ...s.expandedAssetIds }
      delete nextExpanded[id]
      // reviewer 6/10 A-7: previously left every `ingestJobs` entry for
      // this asset behind untouched (the server DELETE route already
      // removed them — routes/assets.ts's DELETE — but this store's own
      // copy didn't follow), despite the doc comment above claiming
      // "clears any expand/ingest-job bookkeeping". Actually drop them
      // now, matching that claim: nothing left to poll for an id that no
      // longer exists, and `useIngestOverlay`/`useAssetBusy` (AssetCard.tsx)
      // would otherwise keep a stale entry around forever (harmless today
      // since neither is ever consulted again for a deleted id, but a
      // dead entry with no cleanup path either).
      const nextIngestJobs = Object.fromEntries(
        Object.entries(s.ingestJobs).filter(([, job]) => job.assetId !== id),
      )
      return {
        assets: nextAssets,
        assetOrder: s.assetOrder.filter((aid) => aid !== id),
        expandedAssetIds: nextExpanded,
        ingestJobs: nextIngestJobs,
        currentProject: s.currentProject
          ? { ...s.currentProject, assetIds: s.currentProject.assetIds.filter((aid) => aid !== id) }
          : s.currentProject,
      }
    })
  },

  async redetectAsset(id, threshold) {
    const job = await api.redetectAsset(id, threshold)
    set((s) => ({ ingestJobs: { ...s.ingestJobs, [job.id]: job } }))
    pollJob(job.id, (updated) => {
      set((s) => ({ ingestJobs: { ...s.ingestJobs, [job.id]: updated } }))
      if (updated.status === 'done') {
        api
          .getAsset(id)
          .then((asset) => {
            set((s) => ({ assets: { ...s.assets, [asset.id]: asset } }))
          })
          .catch(() => {
            // best-effort refresh — the Job itself already reached 'done';
            // a failed re-fetch here just leaves the asset bin showing the
            // pre-redetect scenes until the next reload/openProject.
          })
      }
    })
  },
}))

/**
 * MAJOR B-1 (reviewer 7/10 差し戻し): cancels `scheduleSave`'s pending
 * debounce timer and, if one was armed, immediately runs `saveNow()` in its
 * place — synchronously from the CALLER's point of view (awaited), so
 * whatever action calls this can rely on the outgoing Composition being
 * persisted before it goes on to switch/duplicate/create/leave against a
 * fresh one. A no-op (returns `true`) when no timer is pending (`saveState`
 * already 'saved'/'idle').
 *
 * m9 (reviewer 9/10 差し戻し残 minor): returns `false` when the flushed
 * `saveNow()` itself failed (`get().saveState === 'error'` afterward).
 * Every caller (`openProject`/`duplicateComposition`/`createComposition`/
 * `backToProjects`) MUST check this and abort (stay put, switch/duplicate/
 * create/leave nothing) rather than continue — continuing would reach each
 * action's own success-path `set(...)` a few lines later (e.g.
 * `openProject`'s `saveState: 'saved'`), overwriting the 'error' the user
 * needs to see with a lie that the dropped edit actually saved fine.
 */
async function flushPendingSave(
  set: (partial: Partial<EditorState>) => void,
  get: () => EditorState,
): Promise<boolean> {
  if (saveTimer) {
    clearTimeout(saveTimer)
    saveTimer = null
    await get().saveNow()
    if (get().saveState === 'error') return false
  }
  return true
}

/** Apply a Composition transform, bump updatedAt, push Undo history, and schedule the debounced save. */
function mutate(
  set: (partial: Partial<EditorState>) => void,
  get: () => EditorState,
  fn: (c: Composition) => Composition,
) {
  const comp = get().composition
  if (!comp) return
  // task spec §2-12: pushed BEFORE the change so Undo restores the state as it
  // was right before this call. HistoryStack's own begin/endBatch bookkeeping
  // collapses a drag's many mutate() calls into a single entry.
  history.push(comp)
  const next = touch(fn(comp))
  set({ composition: next, historyCanUndo: history.canUndo(), historyCanRedo: history.canRedo() })
  get().scheduleSave()
}

/** Clear a deleted item out of both the array and legacy-mirror selection fields. */
function deselectItem(
  set: (partial: Partial<EditorState>) => void,
  get: () => EditorState,
  kind: SelectionItem['kind'],
  id: string,
) {
  const selectedIds = get().selectedIds.filter((x) => !(x.kind === kind && x.id === id))
  if (selectedIds.length !== get().selectedIds.length) {
    set({ selectedIds, selection: selectedIds[0] ?? null })
  }
}

function pollJob(jobId: string, onUpdate: (j: Job) => void, intervalMs = 400) {
  const tick = async () => {
    try {
      const job = await api.getJob(jobId)
      onUpdate(job)
      if (job.status === 'queued' || job.status === 'running') {
        setTimeout(tick, intervalMs)
      }
    } catch {
      // job disappeared or network hiccup — stop polling silently is wrong;
      // surface as an error state via onUpdate so the UI shows it.
      onUpdate({
        id: jobId,
        kind: 'render',
        status: 'error',
        progress: 0,
        log: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        error: 'job polling failed',
      })
    }
  }
  tick()
}
